import { Check, ChevronsUpDown, Clock } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '~/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import type { ProductRevision } from '~/features/explorer/types/products-catalog.types';
import { cn } from '~/utils/cn';
import { formatDate, formatDateTime } from '~/utils/date';

interface RevisionSelectorProps {
  revisions: ProductRevision[];
  /** Selected sync id, or `null` for the latest revision. */
  value: string | null;
  onChange: (syncId: string | null) => void;
  isLoading?: boolean;
  /** Extra classes for the trigger button (e.g. `h-9` to align in a toolbar). */
  triggerClassName?: string;
  /** Extra classes for the trigger's text label — lets a host collapse it to an
   *  icon-only trigger at a breakpoint (e.g. `max-[1199px]:hidden`). */
  labelClassName?: string;
  /** E2E `data-testid` for the trigger; popover/items derive `${testId}-...`. */
  testId?: string;
}

/** Groups revisions by ingestion date (preserving the backend's newest-first
 *  order) so the picker can render a labelled section per day. */
function groupByDate(revisions: ProductRevision[]): { date: string; items: ProductRevision[] }[] {
  const groups: { date: string; items: ProductRevision[] }[] = [];
  for (const rev of revisions) {
    const last = groups[groups.length - 1];
    if (last?.date === rev.ingestionDate) last.items.push(rev);
    else groups.push({ date: rev.ingestionDate, items: [rev] });
  }
  return groups;
}

/**
 * Point-in-time revision (sync) picker. A pinned "Latest" entry selects `null`
 * (newest); other entries pick a specific `syncId`. Threads through the
 * Products / Item Groups / Categories tabs as the scope selector.
 */
export function RevisionSelector({
  revisions,
  value,
  onChange,
  isLoading,
  triggerClassName,
  labelClassName,
  testId = 'revision-selector',
}: RevisionSelectorProps) {
  const { t } = useTranslation('explorer');
  const [open, setOpen] = useState(false);
  const groups = useMemo(() => groupByDate(revisions), [revisions]);
  const selected = value ? revisions.find((r) => r.syncId === value) : null;

  const triggerLabel = (() => {
    if (isLoading) return t('products.loading');
    if (selected)
      return selected.lastIngestedAt
        ? formatDateTime(selected.lastIngestedAt)
        : formatDate(selected.ingestionDate);
    return t('products.revisionLatest');
  })();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          data-testid={testId}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={isLoading}
          className={cn('w-52 cursor-pointer justify-between font-normal', triggerClassName)}
        >
          <span className="flex min-w-0 items-center gap-2">
            <Clock aria-hidden="true" className="text-muted-foreground h-4 w-4 shrink-0" />
            <span data-testid={`${testId}-label`} className={cn('truncate', labelClassName)}>
              {triggerLabel}
            </span>
          </span>
          <ChevronsUpDown className="text-muted-foreground h-4 w-4 shrink-0 opacity-70" />
        </Button>
      </PopoverTrigger>
      <PopoverContent data-testid={`${testId}-popover`} className="w-64 p-0" align="start">
        <Command>
          <CommandInput
            data-testid={`${testId}-search-input`}
            placeholder={t('products.searchRevisions')}
          />
          <CommandList>
            <CommandEmpty>{t('products.noRevisionsFound')}</CommandEmpty>
            {/* Pinned "Latest" — selects null (newest revision). */}
            <CommandGroup>
              <CommandItem
                data-testid={`${testId}-item-latest`}
                value="latest"
                onSelect={() => {
                  onChange(null);
                  setOpen(false);
                }}
                className="cursor-pointer"
              >
                <Check
                  className={cn('h-4 w-4 shrink-0', value === null ? 'opacity-100' : 'opacity-0')}
                />
                <span className="font-medium">{t('products.revisionLatest')}</span>
              </CommandItem>
            </CommandGroup>
            {groups.map((group, gi) => (
              <CommandGroup key={`${group.date}-${String(gi)}`} heading={formatDate(group.date)}>
                {group.items.map((rev, ii) => {
                  // Unique per entry so cmdk never collapses two revisions that
                  // share an ingestion date (or sync id) into a single row.
                  const uid = `${String(gi)}-${String(ii)}`;
                  return (
                    <CommandItem
                      key={`${rev.syncId}-${uid}`}
                      data-testid={`${testId}-item-${rev.syncId}`}
                      value={`${rev.ingestionDate} ${rev.syncId} ${uid}`}
                      onSelect={() => {
                        onChange(rev.syncId);
                        setOpen(false);
                      }}
                      className="cursor-pointer"
                    >
                      <Check
                        className={cn(
                          'h-4 w-4 shrink-0',
                          rev.syncId === value ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                      <span className="flex min-w-0 flex-col">
                        <span className="truncate text-sm">
                          {rev.lastIngestedAt
                            ? formatDateTime(rev.lastIngestedAt)
                            : formatDate(rev.ingestionDate)}
                        </span>
                        <span className="text-muted-foreground text-xs">
                          {t('products.revisionProducts', {
                            formattedCount: rev.productCount.toLocaleString(),
                          })}
                          {rev.avgQualityScore != null
                            ? ` ${t('products.revisionAvg', {
                                score: Number(rev.avgQualityScore.toFixed(1)),
                              })}`
                            : ''}
                        </span>
                      </span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
